// /telegram — chats of the personal Telegram accounts the viewer owns
// (our own client, backend/tg_personal.py). Only private chats; history of a
// chat is pulled on demand from the chat pane.
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { cn } from "../lib/utils";
import { Plus, Search, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { TgDialogPane, accountLabel, errText } from "../components/TgChat";
import { toast } from "sonner";

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// Write first to a phone number (not tied to a client card).
function NewChat({ accounts, onStarted }) {
  const [open, setOpen] = useState(false);
  const [acc, setAcc] = useState("");
  const [phone, setPhone] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const usable = accounts.filter((a) => a.connected);
  if (!usable.length) return null;
  const from = acc || usable[0].id;
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/tg/accounts/${from}/start`, { phone, text });
      toast.success("Sent");
      setOpen(false); setPhone(""); setText("");
      onStarted(r.data.dialog_id);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  if (!open) {
    return (
      <Button size="sm" variant="outline" className="h-8" onClick={() => setOpen(true)} data-testid="tg-new-chat">
        <Plus className="h-3.5 w-3.5 mr-1" />New chat
      </Button>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 w-full rounded-lg border border-zinc-800 p-2">
      {usable.length > 1 && (
        <select value={from} onChange={(e) => setAcc(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 text-zinc-300 h-8 rounded-md px-2 text-xs">
          {usable.map((a) => <option key={a.id} value={a.id}>{accountLabel(a)}</option>)}
        </select>
      )}
      <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+380…"
             className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm w-40" />
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="First message"
             onKeyDown={(e) => e.key === "Enter" && phone && text && go()}
             className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm flex-1 min-w-[200px]" />
      <Button size="sm" className="h-8" disabled={busy || !phone || !text.trim()} onClick={go}>Send</Button>
      <button className="text-xs text-zinc-500 hover:text-zinc-300" onClick={() => setOpen(false)}>cancel</button>
      <span className="text-[11px] text-zinc-500 w-full">Max 20 new people per hour per account — Telegram limits cold messaging.</span>
    </div>
  );
}

export default function TelegramInboxPage() {
  const [params, setParams] = useSearchParams();
  const selected = params.get("chat");
  const [account, setAccount] = useState("");
  const [q, setQ] = useState("");
  const [linked, setLinked] = useState("");
  const [data, setData] = useState({ dialogs: [], accounts: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/tg/dialogs", { params: { account_id: account || undefined, q: q || undefined, linked: linked || undefined } });
      setData(r.data);
    } catch (e) { toast.error(errText(e)); } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [account, q, linked]);
  // New messages arrive live on the server; refresh the list while the page is open.
  useEffect(() => { const t = setInterval(load, 20000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [account, q, linked]);

  const select = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set("chat", id); else next.delete("chat");
    setParams(next, { replace: true });
  };
  const chip = (active) => cn("text-xs px-2.5 py-1 rounded-md border",
    active ? "border-sky-500/50 text-sky-300 bg-sky-500/10" : "border-zinc-700 text-zinc-400");

  return (
    <div data-testid="telegram-inbox-page" className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-3">
          <Send className="h-6 w-6 text-sky-400" />Telegram
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Private chats of your Telegram accounts, through the portal's own connection — nothing passes a third party.
        </p>
      </div>

      {!loading && data.accounts.length === 0 && (
        <Card className="bg-zinc-900 border-amber-500/30">
          <CardContent className="py-3 text-sm text-amber-300">
            No Telegram account is assigned to you yet — an admin connects one on Settings → Telegram.
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="h-4 w-4 text-zinc-500 absolute left-2 top-2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, @username, phone"
                 className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm pl-8 w-60" />
        </div>
        {data.accounts.length > 1 && (
          <>
            <button className={chip(!account)} onClick={() => setAccount("")}>All accounts</button>
            {data.accounts.map((a) => (
              <button key={a.id} className={chip(account === a.id)} onClick={() => setAccount(a.id)}>
                {accountLabel(a)}{!a.connected && " · offline"}
              </button>
            ))}
          </>
        )}
        <span className="w-px h-5 bg-zinc-800 mx-1" />
        {[["", "All"], ["yes", "Clients"], ["no", "Not linked"]].map(([v, l]) => (
          <button key={v} className={chip(linked === v)} onClick={() => setLinked(v)}>{l}</button>
        ))}
        <div className="ml-auto"><NewChat accounts={data.accounts} onStarted={(id) => { load(); select(id); }} /></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
          <div className="max-h-[72vh] overflow-y-auto divide-y divide-zinc-800">
            {loading ? <p className="text-zinc-500 py-8 text-center text-sm">Loading…</p>
              : data.dialogs.length === 0 ? (
                <p className="text-zinc-500 py-8 text-center text-sm">No chats yet — they appear as messages come in.</p>
              ) : data.dialogs.map((d) => (
                <button key={d.id} onClick={() => select(d.id)}
                        className={cn("w-full text-left px-3 py-2 hover:bg-zinc-800/60", selected === d.id && "bg-sky-500/10")}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-zinc-100 truncate">{d.name || d.username || "—"}</span>
                    {data.accounts.length > 1 && (
                      <span className="text-[10px] text-zinc-500 truncate">
                        {accountLabel(data.accounts.find((a) => a.id === d.account_id))}
                      </span>
                    )}
                    <span className="ml-auto text-[10px] text-zinc-500 shrink-0">{fmt(d.last_message_at)}</span>
                  </div>
                  <div className="text-xs text-zinc-500 truncate mt-0.5">
                    {d.last_direction === "out" ? "You: " : ""}{d.last_text || "—"}
                  </div>
                  {d.client_name && <div className="text-[11px] text-emerald-400 mt-0.5 truncate">↳ {d.client_name}</div>}
                </button>
              ))}
          </div>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            {selected ? <TgDialogPane dialogId={selected} onChanged={load} />
              : <p className="text-zinc-500 py-16 text-center text-sm">Pick a chat on the left</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
